import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Select, 
  message, 
  Space, 
  Divider, 
  Typography,
  Row,
  Col
} from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { handleFetchResponse } from '../utils/authUtils';
import { API_ENDPOINTS } from '../utils/apiConfig';

const { Title, Text } = Typography;
const { Option } = Select;

interface UserManagementProps {
  // This component will be used within Tab A
}

const UserManagement: React.FC<UserManagementProps> = () => {
  const { user } = useAuth();
  const [registerForm] = Form.useForm();
  const [registerLoading, setRegisterLoading] = useState(false);
  const [teams, setTeams] = useState<Array<{ _id: string; code: string }>>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // Check if current user has permission to manage users
  const canManageUsers = user?.role === 'Admin' || user?.role === 'Leader';

  /**
   * Fetch teams for team selection
   */
  const fetchTeams = async () => {
    if (user?.role !== 'Admin') return; // Only Admin can fetch teams
    
    setTeamsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TEAMS_ALL, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      await handleFetchResponse(
        response,
        (data: Array<{ _id: string; code: string }>) => {
          setTeams(data);
        },
        (data) => {
          message.error(data.message || 'Failed to load teams');
        }
      );
    } catch (error) {
      console.error('Error fetching teams:', error);
      message.error('An error occurred while loading teams');
    } finally {
      setTeamsLoading(false);
    }
  };

  // Load teams on component mount (Admin only)
  useEffect(() => {
    if (user?.role === 'Admin') {
      fetchTeams();
    }
  }, [user]);

  // Set team field to leader's team and disable when user is a Leader
  useEffect(() => {
    if (user?.role === 'Leader') {
      // Set team field to leader's team and disable
      registerForm.setFieldsValue({ team: user.team, role: 'Employee' });
    }
  }, [user, registerForm]);

  /**
   * Handle user registration form submission
   * @param values - Form values containing user registration data
   */
  const handleRegisterUser = async (values: any) => {
    if (!canManageUsers) {
      message.error('You do not have permission to register users');
      return;
    }

    setRegisterLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Nếu là Leader, ép team gửi lên là team của leader
      let submitValues = { ...values };
      if (user?.role === 'Leader') {
        submitValues.team = user.team;
      }
      
      // Convert username to lowercase
      submitValues.username = submitValues.username.toLowerCase();
      const response = await fetch(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitValues),
      });

      await handleFetchResponse(
        response,
        (data) => {
          message.success('User registered successfully');
          registerForm.resetFields();
          if (user?.role === 'Leader') {
            registerForm.setFieldsValue({ team: user.team });
          }
        },
        (data) => {
          message.error(data.message || 'Failed to register user');
        }
      );
    } catch (error) {
      console.error('Error registering user:', error);
      message.error('An error occurred while registering the user');
    } finally {
      setRegisterLoading(false);
    }
  };

  // If user doesn't have permission, show access denied message
  if (!canManageUsers) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Title level={4} style={{ color: '#ff4d4f' }}>
            Access Denied
          </Title>
          <Text type="secondary">
            You do not have permission to access user management features.
            Only Admin and Leader roles can manage users.
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}>User Management</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
        Register new user accounts. Password reset functionality is available in the data tables via action buttons.
      </Text>

      <Row justify="center">
        <Col xs={24} sm={20} md={16} lg={12}>
          <Card 
            title={
              <Space>
                <UserAddOutlined />
                Register New User
              </Space>
            }
            style={{ maxWidth: '600px' }}
          >
            <Form
              form={registerForm}
              layout="vertical"
              onFinish={handleRegisterUser}
              autoComplete="off"
              initialValues={user?.role === 'Leader' ? { role: 'Employee' } : {}}
            >
              <Form.Item
                name="username"
                label="Username"
                rules={[
                  { required: true, message: 'Please enter a username' },
                  { min: 3, message: 'Username must be at least 3 characters' }
                ]}
              >
                <Input placeholder="Enter username" />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please enter a password' },
                  { min: 6, message: 'Password must be at least 6 characters' }
                ]}
              >
                <Input.Password placeholder="Enter password" />
              </Form.Item>

              <Form.Item
                name="role"
                label="Role"
                rules={[
                  { required: true, message: 'Please select a role' }
                ]}
              >
                <Select
                  placeholder="Select role"
                  onChange={(value) => {
                    // Clear team when role changes
                    if (value === 'Admin') {
                      registerForm.setFieldsValue({ team: undefined });
                    }
                  }}
                  disabled={user?.role === 'Leader'} // Leader cannot change role
                >
                  {user?.role === 'Leader' ? (
                    <Option value="Employee">Employee</Option>
                  ) : (
                    <>
                      <Option value="Admin">Admin</Option>
                      <Option value="Leader">Leader</Option>
                      <Option value="Employee">Employee</Option>
                    </>
                  )}
                </Select>
              </Form.Item>

              <Form.Item
                name="team"
                label="Team"
                dependencies={['role']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const role = getFieldValue('role');
                      if (role !== 'Admin' && !value) {
                        return Promise.reject(new Error('Team is required for Leader and Employee roles'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Select
                  placeholder="Select team"
                  loading={teamsLoading}
                  disabled={user?.role === 'Leader' || registerForm.getFieldValue('role') === 'Admin'}
                  value={user?.role === 'Leader' ? user.team : undefined}
                >
                  {user?.role === 'Leader' ? (
                    <Option value={user.team}>{user.team}</Option>
                  ) : (
                    teams.map(team => (
                      <Option key={team._id} value={team.code}>
                        {team.code}
                      </Option>
                    ))
                  )}
                </Select>
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={registerLoading}
                  block
                >
                  Register User
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Information Notice */}
      <Card style={{ backgroundColor: '#e6f7ff', borderColor: '#91d5ff' }}>
        <Title level={5} style={{ color: '#1890ff', marginBottom: '8px' }}>
          Password Reset Information
        </Title>
        <Text type="secondary">
          • Password reset functionality is available in all data tables.<br />
          • Look for the key icon (🔑) in the Actions column of any data table.<br />
          • Click the key icon next to any user to reset their password.<br />
          • Only Admin and Leader roles can perform password resets.
        </Text>
      </Card>

      <Divider />

      {/* Security Notice */}
      <Card style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
        <Title level={5} style={{ color: '#52c41a', marginBottom: '8px' }}>
          Security Notice
        </Title>
        <Text type="secondary">
          • User registration operations are logged for security purposes.<br />
          • Only Admin and Leader roles can perform these operations.<br />
          • Passwords are securely hashed before storage.<br />
          • All operations require valid authentication tokens.
        </Text>
      </Card>
    </div>
  );
};

export default UserManagement; 